/**
 * Positioning maths for dragging the guided-help coach-mark around (KEY-446).
 *
 * The coach-mark is placed automatically beside whatever control the current step spotlights, so —
 * unlike the Help videos dock in `dockPosition.ts` — its position is not a remembered point but an
 * *offset* from that computed placement. Two consequences, and they are the whole reason this is a
 * separate module rather than a reuse of `useDraggableDock`:
 *
 * - **Nothing is persisted.** The offset belongs to the step you are looking at. Restoring last
 *   week's drag onto a different control on a different page would put the card nowhere useful.
 * - **It survives the target moving.** Because it's an offset, the card keeps the distance you gave
 *   it while the page scrolls and the spotlight tracks its control.
 *
 * What is shared with the dock is the rule that matters: a panel must never be dragged somewhere it
 * can't be grabbed back from — hence `MIN_VISIBLE_PX`, re-applied on every move and on resize.
 */

import { MIN_VISIBLE_PX } from "./dockPosition";

export interface DragOffset {
  dx: number;
  dy: number;
}

export interface Point {
  top: number;
  left: number;
}

export interface Size {
  width: number;
  height: number;
}

export const NO_DRAG: DragOffset = { dx: 0, dy: 0 };

/**
 * Keeps the card grabbable: `left` may go negative so its right-hand edge can be pushed off to the
 * right, as long as `MIN_VISIBLE_PX` of it stays on screen; `top` is bounded at 0 because the drag
 * handle is the card's header — above zero there is nothing left to take hold of.
 */
export function clampOverlayPosition(desired: Point, card: Size, viewport: Size): Point {
  return {
    left: Math.min(Math.max(desired.left, MIN_VISIBLE_PX - card.width), Math.max(viewport.width - MIN_VISIBLE_PX, 0)),
    top: Math.min(Math.max(desired.top, 0), Math.max(viewport.height - MIN_VISIBLE_PX, 0)),
  };
}

/**
 * The offset after dragging by `delta` from where the card sat when the pointer went down.
 *
 * `origin` is the card's real on-screen position at pointer-down plus the offset it already carried,
 * so this works identically whether the card is placed beside a target or centred (no target found)
 * — neither caller has to know which.
 */
export function nextDragOffset(
  origin: Point & { offset: DragOffset },
  delta: DragOffset,
  card: Size,
  viewport: Size,
): DragOffset {
  const clamped = clampOverlayPosition({ top: origin.top + delta.dy, left: origin.left + delta.dx }, card, viewport);
  return {
    dx: origin.offset.dx + (clamped.left - origin.left),
    dy: origin.offset.dy + (clamped.top - origin.top),
  };
}

/**
 * Re-clamps an existing offset against a fresh placement — used when the window resizes or the
 * spotlighted control moves, either of which can strand a card that was dragged near an edge.
 */
export function clampOffsetAt(base: Point, offset: DragOffset, card: Size, viewport: Size): DragOffset {
  const clamped = clampOverlayPosition({ top: base.top + offset.dy, left: base.left + offset.dx }, card, viewport);
  return { dx: clamped.left - base.left, dy: clamped.top - base.top };
}

/**
 * The offset correction that brings a card's rendered rect back on screen — zero when it already is.
 * Used on window resize, where the card's own rect is the only placement information available: it
 * works the same whether the card was positioned beside a target or centred.
 */
export function offsetCorrection(rect: Point & Size, viewport: Size): DragOffset {
  const clamped = clampOverlayPosition({ top: rect.top, left: rect.left }, rect, viewport);
  return { dx: clamped.left - rect.left, dy: clamped.top - rect.top };
}

export function sameOffset(a: DragOffset, b: DragOffset): boolean {
  return a.dx === b.dx && a.dy === b.dy;
}
